// eslint-disable-next-line import/no-named-as-default
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { ApolloError } from "@apollo/client";
import {
  FilterProductLabelInputGroup,
  FilterProductLabelResult,
  PageContentType,
  ProductLabel,
  ProductLabelInput,
  WishlistProduct,
  type CatalogProduct,
} from "src/types/graphql.d";
import { useMemo } from "react";
import { pick } from "lodash-es";

interface FilteredProductLabels {
  filterProductLabels: FilterProductLabelResult;
}

interface ProductLabelVars {
  items: FilterProductLabelInputGroup[];
  page_content_type: PageContentType;
}

export interface ProductFilteredLabels {
  [key: string]: ProductLabel[];
}

interface FilteredProductLabelsResponse {
  data: ProductFilteredLabels;
  loading: boolean;
  error?: ApolloError;
}

type Product = CatalogProduct | WishlistProduct;

const PRODUCT_LABEL_INPUT_FIELDS: (keyof ProductLabelInput)[] = [
  "type",
  "color",
  "background_color",
  "text_color",
  "priority",
  "value",
  "expires_at",
  "expires_at_timestamp",
  "starts_at",
  "starts_at_timestamp",
];

export const FILTER_PRODUCT_LABELS = gql`
  query filterProductLabels(
    $items: [FilterProductLabelInputGroup]
    $page_content_type: PageContentType
  ) {
    filterProductLabels(items: $items, page_content_type: $page_content_type) {
      items {
        slug
        labels {
          type
          color
          background_color
          text_color
          priority
          value
          expires_at
          starts_at
        }
      }
    }
  }
`;

const useFilterProductLabels = ({
  products,
  pageContentType,
}: {
  products: CatalogProduct[];
  pageContentType: PageContentType;
}): FilteredProductLabelsResponse => {
  const nonNullableItems = useMemo(
    () => (products?.filter((product) => Boolean(product)) as Product[]) || [],
    [products]
  ); // Need to trick TS here, there is a definitely no way to get null[] as output

  const items: FilterProductLabelInputGroup[] = useMemo(
    () =>
      nonNullableItems.map((item) => ({
        slug: item?.slug,
        labels:
          item?.labels?.map(
            (label) =>
              pick(label, PRODUCT_LABEL_INPUT_FIELDS) as ProductLabelInput
          ) || [],
      })),
    [nonNullableItems]
  );

  const { data, loading, error } = useQuery<
    FilteredProductLabels,
    ProductLabelVars
  >(FILTER_PRODUCT_LABELS, {
    variables: {
      items,
      page_content_type: pageContentType,
    },
    fetchPolicy: "cache-first",
    context: {
      usePOST: true,
    },
  });

  const filters: ProductFilteredLabels = useMemo(
    () =>
      data?.filterProductLabels?.items?.reduce(
        (filteredLabels: ProductFilteredLabels, product) => {
          if (product) {
            filteredLabels[product.slug] = (product.labels ||
              []) as ProductLabel[];
          }
          return filteredLabels;
        },
        {}
      ) || {},
    [data?.filterProductLabels?.items]
  );

  return {
    data: filters,
    loading,
    error,
  };
};
export default useFilterProductLabels;
